import { Button } from "@mui/material"; // Import MUI Button
import {
  SearchContainer,
  SearchBar,
  StyledInputBase,
} from "core/components/global/advanceSearch/advanceSearch.styled";
import { useState, useEffect, useContext } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { formatMessageTime } from "core/utils/dateTimeUtils";
import { ConversationType } from "../chat.type";
import { CustomCheckBoxStyle, FullHistoryButton, FullHistoryContainer, HistorySelectedCount } from "../chat.styled";
import { ArrowRight } from "assets/ArrowRight";
import { IconButton, styled, Box } from "@mui/material";
import DeleteHistoryItemModal from "./deleteHistoryItemModal";
import { deleteConversationFromApi } from "modules/chat/services/chatService";
import { removeConversation } from "modules/chat/store/conversationList";
import { AuthContext } from "core/contexts/auth/auth";
import { useAppDispatch } from "core/store/reduxStore";
import { SidebarContext } from "core/contexts/sidebar/sidebar";
import { useAppTheme } from "core/contexts/theme/theme";
import { useTheme } from "styled-components";



const CustomCheckbox = ({
  checked = false,
  checkedColor = "#4F46E5",
  toggleSelect,
  conversation,
}) => {
  const {apptheme} = useAppTheme();
  return (
    <CustomCheckBoxStyle>
      <div
        className={`w-5 h-5 rounded cursor-pointer`}
        style={{
          backgroundColor: checked ? checkedColor : apptheme.colors.uncheckedColor || "#2B313A",
          transition: "background-color 0.2s ease border",
        }}
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onClick={() => toggleSelect(conversation.conversationId)}
      >
        {checked && (
          <svg
            className="text-white"
            width="100%"
            height="100%"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </div>
    </CustomCheckBoxStyle>
  );
};

export default function ChatHistoryFull({
  convoState,
  setConversationFromHistory,
  setShowCompleteHistory
}) {
  const placeholder = "Search your chats...";
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [conversations, setConversations] = useState<ConversationType[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [groupedConversations, setGroupedConversations] = useState<{
    [key: string]: ConversationType[];
  }>({});
  const [filteredConversations, setFilteredConversations] = useState<{
    [key: string]: ConversationType[];
  } | null>(null);

  const [selectedConversations, setSelectedConversations] = useState<{
    [key: string]: boolean;
  }>({});

  const [displayConversations, setDisplayConversations] = useState<{
    [key: string]: ConversationType[];
  }>({});

  const [selectedConversationsCount, setSelectedConversationsCount] =
    useState(0);
  const { checkAuthToken } = useContext(AuthContext);
  const { modelData } = useContext(SidebarContext);
  const dispatch = useAppDispatch();

  // Handle delete button click
  const deleteSelectedConversations = () => {
    const selectedIds = Object.keys(selectedConversations).filter(
      (id) => selectedConversations[id]
    );

    selectedIds.map((id) => {
      deleteConversation(id);
    });

    deselectAllConversations();
  };

  const handleDelete = () => {
    handleClose();
    deleteSelectedConversations();
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  async function deleteConvoAsync(id: string) {
    const token = await checkAuthToken();

    const response = await deleteConversationFromApi(token, id);

    // Update the frontend if the conversation is truly deleted
    if (response) {
      if (response.status < 300) {
        // Remove the conversation from the Conversation Redux Slice
        dispatch(removeConversation({ conversationId: id }));
      }
    }
  }

  /**
   * Return the provider obj for the name
   */
  function getProviderDescriptionByName(name: string) {
    return convoState.providers?.find((provider) => provider.name === name)
      ?.options.description;
  }

  /**
   * Delete the selected conversation
   */
  const deleteConversation = async (id: string) => {
    if (id) {
      // The conversation must be cleared
      // 1) Delete the conversation in the backend
      await deleteConvoAsync(id);
    }
  };

  /**
   * Fetch the sidebar name for the current model
   */
  function findObjectByParameter(data, targetValue) {

    // Handle the case when data is an array (like the root sidebarData)
    if (Array.isArray(data)) {
      for (const item of data) {
        const result = findObjectByParameter(item, targetValue);
        if (result) return result;
      }
      return null;
    }

    // Handle the case when data is an object
    if (data && typeof data === "object") {
      // Check if this object has the parameter matching the target value
      if (data.parameter === targetValue) {
        return data;
      }

      // Search through all properties of the object
      for (const key in data) {
        // Skip if the property is not an object or array
        if (typeof data[key] !== "object" || data[key] === null) {
          continue;
        }

        const result = findObjectByParameter(data[key], targetValue);
        if (result) return result;
      }
    }

    // If we get here, no match was found
    return null;
  }

  // Handle search functionality
  const handleSearch = () => {
    if (!searchValue || searchValue === "") {
      setFilteredConversations(null);
    } else {
      const searchTermLower = searchValue.toLowerCase();

      const filteredGroups = Object.entries(groupedConversations).reduce(
        (acc, [modelName, groupConversations]) => {
          const filteredConversations = groupConversations.filter(
            (conversation) => {
              // Check if search term is in the conversation title
              const titleMatch = conversation.conversationTitle
                .toLowerCase()
                .includes(searchTermLower);
      
              // Check if search term is in any message content
              const messageMatch = conversation.messages.some((message) => {
                // Skip the message if it's undefined or doesn't have a 'content' key
                if (!message || !message.hasOwnProperty("content")) {
                  return false;
                }

                // Handle different content types
                if (typeof message.content === "string") {
                  return message.content
                    .toLowerCase()
                    .includes(searchTermLower);
                } else if (message.content && typeof message.content === "object") {
                  // If content is an object (ContentType), try to find matches in relevant fields
                  const contentStr = JSON.stringify(message.content).toLowerCase();
                  return contentStr.includes(searchTermLower);
                }
                return false;
              });
      
              // Return true if the search term matches either title or message content
              return titleMatch || messageMatch;
            }
          );
      
          if (filteredConversations.length > 0) {
            acc[modelName] = filteredConversations;
          }
          return acc;
        },
        {}
      );        

      setFilteredConversations(filteredGroups);
    }
  };

  const handleResetSearch = () => {
    setSearchValue("");
    setFilteredConversations(null);
  };

  // Toggle selection of a conversation
  const toggleSelect = (id) => {
    setSelectedConversations((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));

    // Update count after state update
    setTimeout(() => countConversations(), 0);
  };

  function countConversations() {
    let count = 1;
    Object.values(selectedConversations).forEach((isSelected) => {
      if (isSelected) count++;
    });
    setSelectedConversationsCount(count);
  }

  function setGroupsFromConversations() {
    const groups: any = Object.values(convoState.history).reduce(
      (acc: any, conversation: ConversationType) => {
        const model = conversation.model || "Unknown";

        if (!acc[model]) {
          acc[model] = [];
        }
        acc[model].push(conversation);
        return acc;
      },
      {}
    );

    setGroupedConversations(groups);
  }

  // Add this function after the countConversations function
  function selectAllConversations() {
    const allConversations = {};
    const conversationsToUse = filteredConversations || groupedConversations;

    // Loop through all conversation groups and select each conversation
    Object.values(conversationsToUse).forEach((group) => {
      group.forEach((conversation) => {
        allConversations[conversation.conversationId] = true;
      });
    });

    setSelectedConversations(allConversations);

    // Update count after selecting all
    setTimeout(() => {
      const count = Object.keys(allConversations).length;
      setSelectedConversationsCount(count);
    }, 0);
  }

  // Add this function to handle deselecting all
  function deselectAllConversations() {
    setSelectedConversations({});
    setSelectedConversationsCount(0);
  }

  useEffect(() => {
    if (Object.values(convoState.history).length >= 0) {
      setConversations(convoState.history);
      setGroupsFromConversations();
    }
  }, [convoState.history]);

  // Add this effect
  useEffect(() => {
    handleSearch();
  }, [searchValue]);

  // Get the conversations to display (filtered or grouped)
  useEffect(() => {
    setDisplayConversations(filteredConversations || groupedConversations);
  }, [filteredConversations, groupedConversations]);

  useEffect(() => {}, [displayConversations]);

  // Calculate the total number of conversations across all groups
  const totalConversations = displayConversations
    ? Object.values(displayConversations).reduce(
        (total, group) => total + group.length,
        0
      )
    : 0;

  return (
    <FullHistoryContainer>
      <div
        className="mt-4 col-12 col-sm-10 col-md-12"
        style={{ maxWidth: "846px", padding: "0px 2rem" }}
      >
        <div>
          <div className="d-flex justify-between cursor-pointer">
          <h1 className="text-text-200 flex items-center gap-2 text-center font-medium text-xl mt-3 mb-3">
            Your chat history 
          </h1>
 <span className="text-right" onClick={()=>setShowCompleteHistory(false)}> <CloseIcon /></span>
 </div>
          {/* Search bar */}
          <SearchContainer className="w-100">
            <SearchBar>
              <StyledInputBase
                placeholder={placeholder}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
              />
            </SearchBar>

            {/* Clear and search icon */}
            <CloseIcon onClick={handleResetSearch} />
            <SearchIcon className="me-2" onClick={handleSearch} />
          </SearchContainer>
        </div>

        <div
          className="d-flex flex-row justify-between align-items-center mt-4"
          style={{ minHeight: "36.5px" }}
        >
          {/* Conditional render of Delete button */}
          {Object.values(selectedConversations).includes(true) ? (
            <div className="w-100 d-flex flex-row align-items-center justify-content-between">
              <div>
                <h1 className="gap-2 font-medium text-sm d-flex flex-row">
                  <p className="mt-0">
                    Total conversations {totalConversations}
                  </p>
                  <div className="text-accent-secondary-000 flex items-center gap-1.5 flex-1">
                    <HistorySelectedCount className="mt-0">
                      ( {selectedConversationsCount}
                    </HistorySelectedCount>
                    <HistorySelectedCount className="mt-0">
                      selected
                    </HistorySelectedCount>
                    <HistorySelectedCount className="mt-0">
                      conversations )
                    </HistorySelectedCount>
                  </div>
                </h1>
              </div>
              <div className="d-flex justify-content-center">
                <Button variant="text" onClick={selectAllConversations}>
                  Select all
                </Button>
                <span className="p-1" />
                <Button
                  variant="text"
                  onClick={deselectAllConversations}
                >
                  Cancel
                </Button>
                <span className="p-1" />
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: "#e03e34",
                  }}
                  onClick={() => setOpenModal(true)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <h1 className="gap-2 font-medium text-sm d-flex flex-row">
                <p className="mt-0">Total conversations {totalConversations}</p>
              </h1>
            </div>
          )}
        </div>

        <div className="py-1 px-1"></div>
      </div>

      <div
        className="col-12 col-sm-10 col-md-12 d-flex flex-column align-items-center"
        style={{
          maxWidth: "846px",
          overflow: "auto",
          padding: "0 2rem",
          height: "100%",
        }}
      >
        <div className="d-flex flex-column w-100">
          {displayConversations &&
            Object.entries(displayConversations).map(
              ([modelName, groupConversations]) => {
                const description = getProviderDescriptionByName(modelName);
                const sidebarObj = findObjectByParameter(modelData, modelName);

                return (
                  <div key={modelName}>
                    <h1 className="gap-2 font-medium text-sm mt-6">
                      {sidebarObj ? sidebarObj.sidebarProps.displayText : modelName} ({groupConversations.length})
                    </h1>
                    
                    {description !== modelName && (
                      <h1 className="gap-2 font-medium text-sm mt-1 mb-3">
                        {description}
                      </h1>
                    )}

                    {/* Conversations from the group */}
                    <div className="space-y-3">
                      {groupConversations.map((conversation: any) => {
                        const isSelected =
                          selectedConversations[conversation.conversationId] ||
                          false;
                        return (
                          <FullHistoryButton
                            style={{
                              border: "1px solid",
                              borderColor: isSelected ? "#6495ed" : "#464646",
                              overflow: "visible",
                            }}
                            key={conversation.conversationId}
                            className={`
                            rounded-xl w-100 transition-all duration-600 cursor-pointer 
                            inline-flex flex-col items-start justify-center 
                            font-medium text-slate-400 active:outline-none
                          `}
                          >
                            <CustomCheckbox
                              checked={isSelected}
                              toggleSelect={toggleSelect}
                              conversation={conversation}
                            />

                            {/* Conversation card content */}
                            <div className="flex d-flex justify-content-between flex-row w-100">
                              <div
                                className="flex-1 py-3 px-4 d-flex justify-content-start flex-column"
                                onClick={() =>
                                  toggleSelect(conversation.conversationId)
                                }
                              >
                                <h2 className="text-start text-base font-medium">
                                  {conversation.conversationTitle || "Untitled"}
                                </h2>
                                <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                                  <span>
                                    {formatMessageTime(
                                      conversation.updatedAt || "unknown"
                                    )}
                                  </span>
                                </div>
                              </div>
                              <div className="d-flex py-3 px-4 justify-content-end align-items-center flex-row">
                                <IconButton
                                  aria-label="send prompt"
                                  edge="end"
                                  onClick={() =>
                                    setConversationFromHistory(
                                      conversation.conversationId,
                                      conversation.model
                                    )
                                  }
                                >
                                  <ArrowRight />
                                </IconButton>
                              </div>
                            </div>
                          </FullHistoryButton>
                        );
                      })}
                    </div>
                  </div>
                );
              }
            )}
        </div>
        <div className="py-4 px-4"></div>
      </div>
      <DeleteHistoryItemModal
        openModal={openModal}
        handleModalClose={handleClose}
        handleDelete={handleDelete}
      />
    </FullHistoryContainer>
  );
}
